import MuiDialog from 'components/MuiDialog';
import { Typography, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { SurveyDialogProps, TProcessRecord } from 'routes/Main/Survey/type';
import useSurvey from 'store/survey/useSurvey';
import useNotification from 'hooks/useNotification';
import SectionTitle from '../../components/SectionTitle';
import MuiTextField from 'components/Form/MuiTextField';
import { useCallback, useEffect, useState } from 'react';
import ProcessDetail from './ProcessDetail';
import SimulPatientInfo from '../SimulPatientInfo';
import { getSimulationRecord, updateSimulationRecord } from 'apis/survey';

const ProcessRecord = (props: SurveyDialogProps<TProcessRecord>) => {
  const [processRecord, setProcessRecord] = useState<any>();
  const {
    title,
    isOpen,
    disabled,
    defaultValues,
    user_id,
    nurseName,
    patientInfo,
    onClose,
  } = props;

  const { onUpdateIsSave } = useSurvey();
  const { onSuccess, onFail, onResultCode, onRequired } = useNotification();

  const { handleSubmit, register, getValues, setValue, watch, control, reset } =
    useForm({
      defaultValues,
    });

  useEffect(() => {
    if (processRecord) {
      reset({
        pr_subject_name: processRecord.pr_subject_name,
        pr_practice_date: processRecord.pr_practice_date,
        pr_scenario: processRecord.pr_scenario,
        pr_professor: processRecord.pr_professor,
        pr_practice_group: processRecord.pr_practice_group,
        pr_team_name: processRecord.pr_team_name,
        pr_nurse1: processRecord.pr_nurse1,
        pr_nurse2: processRecord.pr_nurse2,
        pr_nurse3: processRecord.pr_nurse3,
        pr_sign_symptom: processRecord.pr_sign_symptom,
        pr_problem: processRecord.pr_problem,
        pr_care: processRecord.pr_care,
        pr_diagnosis: processRecord.pr_diagnosis,
      });
    }
  }, [processRecord, reset]);

  const onSubmit = (data: any) => {
    const request = {
      user_id,
      patient_id: patientInfo.patient_id,
      simulation_record_info: { ...data },
    };
    updateSimulationRecord(request)
      .then(({ data: { rc } }) => {
        if (rc !== 1) {
          onResultCode(rc);
        } else {
          onUpdateIsSave(true);
          onSuccess('저장되었습니다');
        }
      })
      .catch(e => onFail('저장에 실패하였습니다', e));
  };

  const formProps = {
    disabled,
    watch,
    register,
    getValues,
    setValue,
    onSuccess,
    onRequired,
    processRecord,
    nurseName,
  };

  const onGetProcessRecord = useCallback(() => {
    getSimulationRecord({
      patient_id: String(patientInfo.patient_id),
      user_id,
    })
      .then(({ data }) => {
        setProcessRecord(data.simulation_record_info);
      })
      .catch((e: any) => {
        const message = ` 간호과정 기록지 정보를 호출하는데 실패했습니다.`;
        onFail(message, e);
      });
  }, []);

  useEffect(onGetProcessRecord, [onGetProcessRecord]);

  return (
    <MuiDialog.SurveyForm
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={disabled ? undefined : handleSubmit(onSubmit)}
      update_at={defaultValues?.update_at}
    >
      <Grid
        container
        wrap="wrap"
        rowSpacing={5}
        columnSpacing={3}
        sx={{ py: 5, px: 1 }}
      >
        <Typography
          sx={{
            margin: '40px auto 0px auto',
            fontWeight: '700',
            fontSize: '16px',
            textAlign: 'center',
          }}
        >
          시뮬레이션 간호과정 기록지
        </Typography>
        <ProcessDetail {...formProps} />
        <SimulPatientInfo patientInfo={patientInfo} nurseName={nurseName} />
      </Grid>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <SectionTitle title="1. 제시된 시나리오에서 중요한 정보 (Sign and Symptom)" />
        <MuiTextField
          sx={{
            marginTop: '20px',
            whiteSpace: 'pre',
          }}
          required={false}
          value={watch('pr_sign_symptom')}
          {...register('pr_sign_symptom')}
          multiline
          minRows={6}
          onPaste={e => e.preventDefault()}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
              e.preventDefault();
            }
          }}
          onContextMenu={e => e.preventDefault()}
        />
        <SectionTitle title="2. 대처가 필요한 간호문제 도출 (Nursing Problem) " />
        <MuiTextField
          sx={{
            marginTop: '20px',
            whiteSpace: 'pre',
          }}
          value={watch('pr_problem')}
          required={false}
          {...register('pr_problem')}
          multiline
          minRows={6}
          onPaste={e => e.preventDefault()}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
              e.preventDefault();
            }
          }}
          onContextMenu={e => e.preventDefault()}
        />
        <SectionTitle title="3. 문제해결을 위해 필요한 간호중재 및 교육 내용 (Plan of Care)" />
        <MuiTextField
          sx={{
            marginTop: '20px',
            whiteSpace: 'pre',
          }}
          value={watch('pr_care')}
          required={false}
          {...register('pr_care')}
          multiline
          minRows={6}
          onPaste={e => e.preventDefault()}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
              e.preventDefault();
            }
          }}
          onContextMenu={e => e.preventDefault()}
        />
        <SectionTitle title="4. 간호진단 (Nursignd diagnosis)" />
        <MuiTextField
          sx={{
            marginTop: '20px',
            whiteSpace: 'pre',
          }}
          value={watch('pr_diagnosis')}
          required={false}
          {...register('pr_diagnosis')}
          multiline
          minRows={6}
          onPaste={e => e.preventDefault()}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
              e.preventDefault();
            }
          }}
          onContextMenu={e => e.preventDefault()}
        />
      </div>
      <div style={{ width: '100%' }}>
        <span
          style={{
            color: '#14855B',
            float: 'right',
            marginTop: '10px',
            fontSize: '12px',
          }}
        >
          *해당 기록지는 삼육대학교 간호학과 협조를 통해 제작한 기록지입니다.
        </span>
      </div>
    </MuiDialog.SurveyForm>
  );
};

export default ProcessRecord;
